/**
 * Session billing display (`greencharge.billing-display.v1`).
 *
 * The backend attaches `billing.display` to session responses: a view
 * model that already says what kind of session this was, what it cost,
 * who is billed and where billing stands. Every money surface in the
 * app renders that — it never works out "free", "client" or "waived"
 * from a billing mode, a payment status or an amount of zero.
 *
 * `billing` rides on the session under the same key in every shape the
 * app handles: the camelCase `/api/user/sessions` DTO, the snake_case
 * `/api/ev/sessions/:uid` row, and the flat record produced by
 * `adaptAccountSession`.
 *
 * When `billing` is absent (older API, flag off, session still in
 * flight) the fallback shows the recorded amount under "Session amount"
 * and the payment state as recorded. Nothing else is inferred.
 */

export const BILLING_DISPLAY_CONTRACT = 'greencharge.billing-display.v1';

const TONES = new Set(['success', 'info', 'warning', 'danger', 'neutral']);

const FALLBACK_AMOUNT_LABEL = 'Session amount';
const FALLBACK_STATUS_LABEL = 'Payment status';

// `payment_status` as the backend records it → plain words. This names
// the state of the driver's payment only; it never names a session type.
const RECORDED_PAYMENT_STATE = {
  paid:        { label: 'Paid',            tone: 'success' },
  captured:    { label: 'Paid',            tone: 'success' },
  authorizing: { label: 'Payment pending', tone: 'info' },
  authorized:  { label: 'Payment pending', tone: 'info' },
  capturing:   { label: 'Payment pending', tone: 'info' },
  error:       { label: 'Payment failed',  tone: 'danger' },
  failed:      { label: 'Payment failed',  tone: 'danger' },
  voiding:     { label: 'Cancelled',       tone: 'neutral' },
  voided:      { label: 'Cancelled',       tone: 'neutral' },
  refunded:    { label: 'Refunded',        tone: 'neutral' },
  bypassed:    { label: 'No payment taken', tone: 'neutral' },
};

const NOT_RECORDED = { label: 'Not recorded', tone: 'neutral' };

function toneOf(tone) {
  return TONES.has(tone) ? tone : 'neutral';
}

function text(value) {
  return typeof value === 'string' && value.trim() ? value : null;
}

/**
 * The server-built display for a session, or null when the response
 * carries none this app can render. Only the customer audience is
 * accepted: an operator display has settlement and waiver detail that
 * must not reach a driver.
 */
export function getBillingDisplay(session) {
  const display = session?.billing?.display;
  if (!display || typeof display !== 'object') return null;
  if (display.contract !== BILLING_DISPLAY_CONTRACT) return null;
  if (display.audience !== 'customer') return null;
  if (!Array.isArray(display.rows) || display.rows.length === 0) return null;
  return display;
}

/** Payment state as recorded, for sessions without `billing`. */
export function recordedPaymentState(paymentStatus) {
  const key = typeof paymentStatus === 'string' ? paymentStatus.trim().toLowerCase() : '';
  return RECORDED_PAYMENT_STATE[key] || NOT_RECORDED;
}

/**
 * Neutral view for a session without `billing`.
 *
 * @param {object} [fallback]
 * @param {*} [fallback.amount] — the recorded amount, already formatted
 *   (a string or a React node). Shown as-is; "—" when missing.
 * @param {string|null} [fallback.paymentStatus] — `payment_status` as recorded.
 * @param {{label: string, tone?: string}} [fallback.status] — overrides the
 *   state derived from `paymentStatus` when the caller holds a more exact one.
 */
export function fallbackBillingDisplay({ amount, paymentStatus, status } = {}) {
  const state = status?.label
    ? { label: status.label, tone: toneOf(status.tone) }
    : recordedPaymentState(paymentStatus);
  const value = amount == null || amount === '' ? '—' : amount;
  return {
    fromServer: false,
    sessionTypeLabel: null,
    summary: null,
    status: state,
    amount: { label: FALLBACK_AMOUNT_LABEL, value },
    rows: [
      { key: 'sessionAmount', label: FALLBACK_AMOUNT_LABEL, value, emphasis: true },
      { key: 'paymentStatus', label: FALLBACK_STATUS_LABEL, value: state.label, tone: state.tone },
    ],
    chargeRows: [],
    customerReceipt: true,
  };
}

/**
 * The one view every billing surface renders.
 *
 * @returns {{
 *   fromServer: boolean,
 *   sessionTypeLabel: string|null,
 *   summary: string|null,
 *   status: {label: string, tone: string},
 *   amount: {label: string, value: *},
 *   rows: Array<{key: string, label: string, value: *, emphasis?: boolean, tone?: string}>,
 *   chargeRows: Array<{key: string, label: string, value: string, minor: number|null}>,
 *   customerReceipt: boolean,
 * }}
 */
export function resolveBillingDisplay(session, fallback) {
  const display = getBillingDisplay(session);
  if (!display) return fallbackBillingDisplay(fallback);

  const usable = (row) => row && text(row.label) && row.value != null && row.value !== '';
  const rows = display.rows.filter(usable).map((row) => ({
    key: row.key || row.label,
    label: row.label,
    value: String(row.value),
    emphasis: row.emphasis === true,
    tone: row.tone ? toneOf(row.tone) : undefined,
  }));
  // Stored charge components (usage, fees). May be empty.
  const chargeRows = (Array.isArray(display.chargeRows) ? display.chargeRows : [])
    .filter(usable)
    .map((row) => ({
      key: row.key || row.label,
      label: row.label,
      value: String(row.value),
      minor: Number.isFinite(row.minor) ? row.minor : null,
    }));

  const headline = display.amount || rows.find((row) => row.emphasis) || null;

  return {
    fromServer: true,
    sessionTypeLabel: text(display.sessionType?.label) || text(display.heading),
    summary: text(display.summary),
    status: {
      label: text(display.status?.label) || NOT_RECORDED.label,
      tone: toneOf(display.status?.tone),
    },
    amount: {
      label: text(headline?.label) || FALLBACK_AMOUNT_LABEL,
      value: text(headline?.value) || '—',
    },
    rows,
    chargeRows,
    customerReceipt: display.receipt?.customerReceipt !== false,
  };
}

/**
 * Whether the standard customer receipt may be sent or offered. Only an
 * explicit `receipt.customerReceipt === false` (a Test session) turns it
 * off; a session without `billing` keeps today's behaviour.
 */
export function offersCustomerReceipt(session) {
  return getBillingDisplay(session)?.receipt?.customerReceipt !== false;
}
